import { useMutation, useQuery } from '@apollo/client';
import React from 'react';
import { useParams } from 'react-router';
import Form, { Field, Submit } from '../../components/Form';
import NotFoundView from '../../error/NotFoundView';
import { gql } from '../../graphql/generated/gql';
import AccountContentContainer from '../AccountContentContainer';
import canAuthenticateWithPassword from '../canAuthenticateWithPassword';
import usePostLoginHandler from '../usePostLoginHandler';
import IntegrationLoginButtons from './IntegrationLoginButtons';
import * as styles from './RegisterWithProvisionView.module.scss';
import {
  PASSWORD_MIN_LENGTH,
  validatePassword,
} from '../../user/settings/UserAccountSettings';

const SIGNUP_WITH_PROVISION_MUTATION = gql(/** @complexity 2 */ `
  mutation registerWithProvision(
    $name: String!
    $email: String!
    $password: String!
    $provisionId: String!
    $provisionSecret: String!
  ) {
    session: registerWithProvision(
      name: $name
      email: $email
      password: $password
      provisionId: $provisionId
      provisionSecret: $provisionSecret
    ) {
      authToken
    }
  }
`);

const VERIFY_PROVISION_SIGNUP_URL_QUERY = gql(/** @complexity 1 */ `
  query verifyProvisionUrl($id: String!, $secret: String!) {
    provision: verifyProvisionUrl(id: $id, secret: $secret) {
      id
      type
    }
  }
`);

type RegisterWithProvisionValues = {
  name: string;
  email: string;
  password: string;
};

const RegisterWithProvisionView = () => {
  const organization = window.__slab.PUBLIC_ORG;
  const { ssoPlatforms } = organization;
  const { provisionId, provisionSecret } = useParams() as {
    provisionId: string;
    provisionSecret: string;
  };

  const { data: provisionData, error } = useQuery(
    VERIFY_PROVISION_SIGNUP_URL_QUERY,
    {
      variables: { id: provisionId, secret: provisionSecret },
    },
  );

  const [registerWithProvision] = useMutation(SIGNUP_WITH_PROVISION_MUTATION);

  const provision = provisionData?.provision;
  const postLoginHandler = usePostLoginHandler();

  const handleSubmit = (values: RegisterWithProvisionValues) =>
    registerWithProvision({
      variables: { ...values, provisionId, provisionSecret },
    }).then(({ data }) => {
      if (data?.session) {
        postLoginHandler(data.session.authToken);
      }
    });

  if (error) {
    return <NotFoundView />;
  }

  if (!provision) {
    return null;
  }

  return (
    <AccountContentContainer
      title={`Welcome to ${organization.name}`}
      subtitle={organization.effectiveHost}
    >
      <IntegrationLoginButtons
        email={organization.email}
        ssoPlatforms={ssoPlatforms}
        provisionId={provisionId}
        provisionSecret={provisionSecret}
      />
      {canAuthenticateWithPassword(
        organization,
        organization.ssoPlatforms,
        provision.type,
      ) && (
        <Form<RegisterWithProvisionValues>
          initialValues={{ name: '', email: '', password: '' }}
          onSubmit={handleSubmit}
          validate={(values) => validatePassword(values.password)}
        >
          {({ isValid }) => (
            <>
              {ssoPlatforms.length > 0 && (
                <>
                  <AccountContentContainer.Divider />
                  <div className={styles.emailText}>
                    Register with your email and password
                  </div>
                </>
              )}
              <Field
                name="name"
                type="text"
                placeholder="Your full name"
                required
                autoComplete="name"
                margin="compact"
              />
              <Field
                name="email"
                type="email"
                placeholder="Your email"
                required
                autoComplete="email username"
                margin="compact"
              />
              <Field
                name="password"
                type="password"
                placeholder="Password"
                required
                autoComplete="new-password"
                margin="compact"
                minLength={PASSWORD_MIN_LENGTH}
              />
              <Submit disabled={!isValid} block>
                Register
              </Submit>
            </>
          )}
        </Form>
      )}
    </AccountContentContainer>
  );
};

export default RegisterWithProvisionView;
